import { httpClient } from "../http-client";
import type {
  ApiResponse,
  KpiStatisticsResponse,
  ProductionLogResponse,
} from "../../../shared/types/api";
import type {
  CompanyKpiMachine,
  CompanyKpiSlot,
  CompanyKpiSlotStat,
  ComputeRequest,
  ComputeResponse,
  CreateKpiSlotRequest,
  MesDashboardCard,
  SaveCardRequest,
  UpdateKpiSlotRequest,
} from "../../../domain/types/company/company-kpi";
import { NEW_API_ENDPOINTS } from "../../../shared/constants/api";

export class CompanyKpiApiService {
  async getStatistics(): Promise<ApiResponse<KpiStatisticsResponse>> {
    const response = await httpClient.get<ApiResponse<KpiStatisticsResponse>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.statistics,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 통계를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getStatisticsPeriod(
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse<KpiStatisticsResponse>> {
    const response = await httpClient.get<ApiResponse<KpiStatisticsResponse>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.statisticsPeriod,
      { params: { startDate, endDate } },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 통계를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getProductionLogs(
    date: string,
    machineIds?: number[],
  ): Promise<ApiResponse<ProductionLogResponse>> {
    const response = await httpClient.get<ApiResponse<ProductionLogResponse>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.productionLogs,
      {
        params: {
          date,
          ...(machineIds && machineIds.length
            ? { machineIds: machineIds.join(",") }
            : {}),
        },
      },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 통계를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getSlotStatistics(
    startDate: string,
    endDate: string,
    machineId?: number,
  ): Promise<ApiResponse<CompanyKpiSlotStat[]>> {
    const response = await httpClient.get<ApiResponse<CompanyKpiSlotStat[]>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.statisticsSlots,
      { params: { startDate, endDate, ...(machineId != null ? { machineId } : {}) } },
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 통계를 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async getMachines(): Promise<ApiResponse<CompanyKpiMachine[]>> {
    const response = await httpClient.get<ApiResponse<CompanyKpiMachine[]>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.machines,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "장비 목록을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }


  async getSlots(): Promise<ApiResponse<CompanyKpiSlot[]>> {
    const response = await httpClient.get<ApiResponse<CompanyKpiSlot[]>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.config,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 항목을 가져오지 못했습니다.",
      );
    }

    return response.data;
  }

  async updateSlot(
    id: number,
    body: UpdateKpiSlotRequest,
  ): Promise<ApiResponse<CompanyKpiSlot>> {
    const response = await httpClient.put<ApiResponse<CompanyKpiSlot>>(
      NEW_API_ENDPOINTS.companyKpiController.PUT.slot(id),
      body,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 항목 수정에 실패했습니다.",
      );
    }

    return response.data;
  }

  async createSlot(
    body: CreateKpiSlotRequest,
  ): Promise<ApiResponse<CompanyKpiSlot>> {
    const response = await httpClient.post<ApiResponse<CompanyKpiSlot>>(
      NEW_API_ENDPOINTS.companyKpiController.POST.slot,
      body,
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 항목 추가에 실패했습니다.",
      );
    }

    return response.data;
  }

  async deleteSlot(id: number): Promise<ApiResponse<unknown>> {
    const response = await httpClient.delete<ApiResponse<unknown>>(
      NEW_API_ENDPOINTS.companyKpiController.DELETE.slot(id),
    );

    if (response.data.errorCode) {
      throw new Error(
        response.data.errorMsg || "KPI 항목 삭제에 실패했습니다.",
      );
    }

    return response.data;
  }

  // ─── KPI 2.6 카드 대시보드 ──────────────────────────────────────────────────

  async compute(body: ComputeRequest): Promise<ApiResponse<ComputeResponse>> {
    const response = await httpClient.post<ApiResponse<ComputeResponse>>(
      NEW_API_ENDPOINTS.companyKpiController.POST.compute,
      body,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "동적 계산에 실패했습니다.");
    }

    return response.data;
  }

  async getCards(): Promise<ApiResponse<MesDashboardCard[]>> {
    const response = await httpClient.get<ApiResponse<MesDashboardCard[]>>(
      NEW_API_ENDPOINTS.companyKpiController.GET.cards,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카드 목록을 가져오지 못했습니다.");
    }

    return response.data;
  }

  async createCard(body: SaveCardRequest): Promise<ApiResponse<MesDashboardCard>> {
    const response = await httpClient.post<ApiResponse<MesDashboardCard>>(
      NEW_API_ENDPOINTS.companyKpiController.POST.card,
      body,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카드 추가에 실패했습니다.");
    }

    return response.data;
  }

  async updateCard(
    id: number,
    body: SaveCardRequest,
  ): Promise<ApiResponse<MesDashboardCard>> {
    const response = await httpClient.put<ApiResponse<MesDashboardCard>>(
      NEW_API_ENDPOINTS.companyKpiController.PUT.card(id),
      body,
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카드 수정에 실패했습니다.");
    }

    return response.data;
  }

  async deleteCard(id: number): Promise<ApiResponse<unknown>> {
    const response = await httpClient.delete<ApiResponse<unknown>>(
      NEW_API_ENDPOINTS.companyKpiController.DELETE.card(id),
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카드 삭제에 실패했습니다.");
    }

    return response.data;
  }

  async reorderCards(cardIds: number[]): Promise<ApiResponse<MesDashboardCard[]>> {
    const response = await httpClient.put<ApiResponse<MesDashboardCard[]>>(
      NEW_API_ENDPOINTS.companyKpiController.PUT.cardsOrder,
      { cardIds },
    );

    if (response.data.errorCode) {
      throw new Error(response.data.errorMsg || "카드 순서 변경에 실패했습니다.");
    }

    return response.data;
  }
}

export const companyKpiApiService = new CompanyKpiApiService();